import { Box, Button, FormControl, HStack, Input, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { signUp } from "../../../api";
import { colors } from "../../../styles";
import { ISignUpCertificate, ISignUpLicense, ISignUpPharmacy, ISignUpUser } from "../../../types";
import { getSignUpErrorMessage } from "./errors";
import StepLayout, { FieldMessage, underlineInputProps } from "./StepLayout";

const PATH_OPTIONS = [
    "지인이 알려줬어요",
    "검색 서비스를 통해 알게 됐어요",
    "SNS를 통해 알게 됐어요",
    "광고 배너를 통해 알게 됐어요",
    "기타",
] as const;

const CUSTOM_PATH_VALUE = "기타";

interface IPathProps {
    progress: number;
    userData: Partial<ISignUpUser>;
    setUserData: React.Dispatch<React.SetStateAction<Partial<ISignUpUser>>>;
    licenseData: Partial<ISignUpLicense>;
    certificateData: Partial<ISignUpCertificate>;
    pharmacyData: Partial<ISignUpPharmacy>;
    onNext: () => void;
}

/* 마지막 단계. 모아 둔 객체를 type에 맞게 붙여 가입을 제출한다. */

export default function Path({
    progress,
    userData,
    setUserData,
    licenseData,
    certificateData,
    pharmacyData,
    onNext,
}: IPathProps) {

    /* 로딩 상태, 에러 메시지, 디바운스된 유입 경로 */

    const [path, setPath] = useState("");
    const [customPath, setCustomPath] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    /* 선택된 유입 경로 */

    const selectedPath = path === CUSTOM_PATH_VALUE ? customPath.trim() : path;

    /* 회원가입 뮤테이션 */

    const signUpMutation = useMutation({
        mutationFn: signUp,
    });

    /* 폼 제출 핸들러 */

    const onSubmit = async () => {
        if (!selectedPath) {
            setErrorMessage("유입 경로를 선택해주세요.");
            return;
        }

        /* 로딩 상태, 에러 메시지 설정 */

        const user = {
            ...userData,
            path: selectedPath,
        } as ISignUpUser;

        /* 로딩 상태, 에러 메시지 설정 */

        setIsLoading(true);
        setErrorMessage("");

        try {

            /* type에 맞는 객체만 붙여서 한 번에 가입 요청 */

            await signUpMutation.mutateAsync({
                user,
                license: user.type === "OWNER" || user.type === "PHARMACIST"
                    ? licenseData as ISignUpLicense
                    : undefined,
                certificate: user.type === "STUDENT"
                    ? certificateData as ISignUpCertificate
                    : undefined,
                pharmacy: user.type === "OWNER"
                    ? pharmacyData as ISignUpPharmacy
                    : undefined,
            });

            /* 회원정보 저장 */

            setUserData(user);

            /* 다음 화면으로 이동 */

            onNext();
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "회원가입 처리 중 문제가 발생했습니다."));
        } finally {
            setIsLoading(false);
        }
    };

    /* render */

    return (
        <StepLayout progress={progress} titles={["팜챗을 어떻게 아셨나요?"]}>

            {/* 유입 경로 선택 폼 */}

            <RadioGroup value={path} onChange={(next) => {
                setPath(next);
                setErrorMessage("");
            }}>
                <Stack spacing={2} width="100%" marginTop={5}>
                    {PATH_OPTIONS.map((option) => (
                        <Box
                            key={option}
                            as="label"
                            cursor="pointer"
                            width="100%"
                            borderRadius={10}
                            px={5}
                            py={4}
                            backgroundColor={path === option ? colors.light : "inherit"}
                            transition="background 0.15s ease"
                        >
                            <HStack spacing={3} align="center">
                                <Radio value={option} colorScheme="blue" />
                                <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                    {option}
                                </Text>
                            </HStack>
                        </Box>
                    ))}
                </Stack>
            </RadioGroup>

            {/* 기타 유입 경로 입력 폼 */}

            {path === CUSTOM_PATH_VALUE && (
                <FormControl marginTop={5}>
                    <Input
                        value={customPath}
                        onChange={(event) => {
                            setCustomPath(event.target.value);
                            setErrorMessage("");
                        }}
                        placeholder="유입 경로를 직접 입력해주세요"
                        {...underlineInputProps}
                    />
                </FormControl>
            )}

            {/* 에러 메시지 */}

            <FieldMessage message={errorMessage} />

            {/* 다음 버튼 */}

            <Button
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={5}
                onClick={onSubmit}
                disabled={!selectedPath}
                isLoading={isLoading}
            >
                <Text fontSize={16} fontWeight={700}>제출</Text>
            </Button>
        </StepLayout>
    );
}
